import { useState, useEffect, useRef, useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { APIProvider } from '@vis.gl/react-google-maps'
import { z } from 'zod'
import { MapPin, Users, CalendarDays, Pencil } from 'lucide-react'
import { EditEventDialog } from '~/client/components/event/EditEventDialog'
import { formatUtcDateLongFromUnknown } from '~/shared/common/date'
import { toast } from 'sonner'
import '~/styles/recs.css'
import {
  extractChips,
  getCityTier,
  getCityVenueCount,
  SUPPORTED_CITIES,
} from '~/client/components/recs/constants'
import type { EventContext, VenueSuggestion } from '~/client/components/recs/constants'

import { RecsInputForm } from '~/client/components/recs/RecsInputForm'
import { RecsPremiumEmptyState } from '~/client/components/recs/RecsPremiumEmptyState'
import { RecsLoadingState } from '~/client/components/recs/RecsLoadingState'
import { RecsSummaryState } from '~/client/components/recs/RecsSummaryState'
import { RecsResultsState } from '~/client/components/recs/RecsResultsState'
import { RecsCompletionState } from '~/client/components/recs/RecsCompletionState'
import { RecsMap } from '~/client/components/recs/RecsMap'
import { useRecsSpaceData } from '~/client/recs/use-recs-space-data.hook'
import { Button } from '~/client/components/ui/button'
import { seo } from '~/client/common/seo'
import { useEvent } from '~/client/event/use-event.hook'
import { useEventAccess } from '~/client/event/use-event-access.hook'
import { useSpaces } from '~/client/space/use-spaces.hook'
import { FeatureFlag, hasFeature } from '~/client/common/features'
import {
  useRevelRecsStatus,
  useRevelRecs,
  useSubmitRevelRecsBrief,
  useSaveVenueReferences,
  useShortlistRevelRec,
  useDownvoteRevelRec,
} from '~/client/recs/use-revel-recs.hook'
import { useAddSpaceToEvent } from '~/client/event/use-add-space-to-event.hook'
import { useListingsStore, selectSpaceBeingAddedToEvent, selectFilters, selectCurrentPage, selectActiveVenueGroupId, selectSortBy } from '~/client/common/stores/listings'
import { countAppliedFilters } from '~/client/common/filters'
import { useAuthStore, useEffectiveUserDetail } from '~/client/common/stores/auth'
import ListingsFiltersPanel from '~/client/components/browse-view/filters/ListingsFiltersPanel'
import ListingsResultsPanel from '~/client/components/browse-view/results/ListingsResultsPanel'
import { ListingsMap } from '~/client/components/browse-view/map/ListingsMap'
import type { FiltersState } from '~/client/common/filters'
import {
  urlParamsToFilters,
  filtersToUrlParams,
  validateSearchParams,
  sortByToUrlParam,
} from '~/client/common/listings-search-params'
import type { ListingsSearchParams } from '~/client/common/listings-search-params'
import { AddEventToSpaceDialog } from '~/client/components/event/EventCreationDialogs'
import { EventViewContent } from '~/client/components/event-view/EventSpreadsheet'
import { NotFound } from '~/client/components/NotFound'

// ── Search params schema ────────────────────────────────────────────────────

const recsSearchSchema = z.object({
  tab: z.enum(['search', 'recs', 'shortlist']).optional().catch(undefined),
  fromCreate: z.boolean().optional().catch(undefined),
})

type RecsSearchParams = z.infer<typeof recsSearchSchema> & ListingsSearchParams

function validateRecsSearch(search: Record<string, unknown>): RecsSearchParams {
  const recs = recsSearchSchema.parse(search)
  const listings = validateSearchParams(search)
  return { ...recs, ...listings }
}

// ── Route definition ────────────────────────────────────────────────────────

export const Route = createFileRoute('/_app/events/$eventSlug')({
  component: RecsPageComponent,
  validateSearch: validateRecsSearch,
  head: () => {
    // Use a static default title to avoid blocking page load
    return {
      meta: seo({
        title: 'Event - Revel Street',
        description: 'View and manage event spaces',
      }),
    }
  },
})

// ── Tab config ──────────────────────────────────────────────────────────────

type VrmTab = 'search' | 'recs' | 'shortlist'

// When true: all three tab panels stay mounted; inactive ones are hidden via
// CSS. Preserves map camera, filter state, scroll position across tab swaps.
// Flip to false to restore conditional rendering (unmounts inactive tabs)
// if the always-mounted trees become a memory/perf problem.
const KEEP_TABS_MOUNTED = true

function TabPanel({
  active,
  keepMounted,
  className,
  children,
}: {
  active: boolean
  keepMounted: boolean
  className?: string
  children: React.ReactNode
}) {
  if (!active && !keepMounted) return null
  return (
    <div className={active ? className : 'hidden'}>
      {children}
    </div>
  )
}

// ── Recs phase ──────────────────────────────────────────────────────────────

type RecsPhase = 'empty' | 'input' | 'loading' | 'in-progress' | 'results' | 'complete'

// ── Location shortener (matches homepage events table logic) ────────────────

function shortenLocation(loc: string | null | undefined): string {
  if (!loc) return '—'
  const supportedCities = Object.keys(SUPPORTED_CITIES)
  const matchedCity = supportedCities.find((city) => loc.includes(city))
  if (matchedCity) {
    const idx = loc.indexOf(matchedCity)
    if (idx > 0) {
      const name = loc.substring(0, idx).replace(/,\s*$/, '').trim()
      if (name) return `${name}, ${matchedCity}`
    }
    return matchedCity
  }
  return loc
}

// ── Event → Filter helpers ──────────────────────────────────────────────────

const STANDING_EVENT_TYPES = new Set([
  'Cocktail Reception',
  'Happy Hour',
  'Holiday Party',
])

const SEATED_EVENT_TYPES = new Set([
  'Dinner',
  'Meeting',
  'Meeting with Breakout Spaces',
  'Breakfast',
  'Lunch',
])

function buildPrivacyMap(privacy: string | undefined | null): Record<string, boolean> {
  const all = {
    'Practically Private': false,
    'Buyout': false,
    'Private': false,
    'Semi-Private': false,
    'Main Dining Area': false,
  }
  if (!privacy) return all

  switch (privacy) {
    case 'Private':
      return { ...all, 'Practically Private': true, Buyout: true, Private: true }
    case 'Semi-Private':
      return { ...all, Buyout: true, 'Semi-Private': true, 'Main Dining Area': true }
    case 'Buyout':
      return { ...all, Buyout: true }
    case 'Flexible':
    default:
      return all
  }
}

// Venue groups that expose the full filter set (must match SpaceFilters.tsx)
const FULL_FILTERS_VENUE_GROUPS = [1, 3, 5, 6] // NYC, SF, Chicago, DC

function buildFiltersFromEvent(
  eventData: any | undefined,
  venueGroupId?: number,
): FiltersState | null {
  if (!eventData) return null

  let standingGuestCount: number | undefined
  let seatedGuestCount: number | undefined
  const guests = eventData.num_attendees
  const eventType = eventData.event_type

  if (guests && eventType) {
    if (eventType === 'Cocktail Reception + Dinner') {
      seatedGuestCount = Math.round(guests * 1.75)
    } else if (STANDING_EVENT_TYPES.has(eventType)) {
      standingGuestCount = guests
    } else if (SEATED_EVENT_TYPES.has(eventType)) {
      seatedGuestCount = guests
    }
  }

  const radiusMiles = eventData.location_max_distance ?? undefined

  // Only auto-apply privacy filters for cities that expose that filter
  // group in the UI.  Other cities hide it, so users can't see or remove it.
  const showAllFilters = !venueGroupId || FULL_FILTERS_VENUE_GROUPS.includes(venueGroupId)
  const privacy = showAllFilters ? eventData.privacy_requirement : undefined

  return {
    location: eventData.location || '',
    locationLat: eventData.latitude ?? undefined,
    locationLng: eventData.longitude ?? undefined,
    radiusMiles,
    vibe: '',
    venueName: '',
    standingGuestCount,
    seatedGuestCount,
    fbMin: undefined,
    fbMax: undefined,
    googlePlaceId: undefined,
    spaceTypes: {},
    privacyTypes: buildPrivacyMap(privacy),
    noisePrivacy: {},
    amenities: {},
    av: {},
    outdoor: {},
    windows: {},
    pricePoints: {},
    google: {},
    michelin: {},
    venueTypes: {},
    venueHighlights: {},
    editorialRecognition: {},
    cuisines: {},
    neighborhoods: {},
  }
}

// ── Component ───────────────────────────────────────────────────────────────

function RecsPageComponent() {
  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places', 'marker']}>
      <RecsPageInner />
    </APIProvider>
  )
}

function RecsPageInner() {
  const { eventSlug } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()

  // ── Load real event data ──────────────────────────────────────────────
  const eventAccessQuery = useEventAccess(eventSlug)
  const { data: eventData, isLoading: eventLoading } = useEvent(eventSlug)
  const numericEventId = eventData?.id ? Number(eventData.id) : undefined
  const isRealEvent = numericEventId != null && !isNaN(numericEventId)

  // ── Revel Recs hooks (persisted via localStorage, ready for Supabase) ─
  const { data: recsStatus } = useRevelRecsStatus(
    isRealEvent ? numericEventId : undefined,
  )
  const { data: recsList } = useRevelRecs(
    isRealEvent ? numericEventId : undefined,
  )
  const submitBriefMutation = useSubmitRevelRecsBrief()
  const saveVenueRefsMutation = useSaveVenueReferences()
  const shortlistRecMutation = useShortlistRevelRec()
  const downvoteRecMutation = useDownvoteRevelRec()
  const addSpaceToEventMutation = useAddSpaceToEvent()

  // ── Derive context from event data + venue groups ─────────────────────
  // EFFECTIVE: entity data (venue groups, city) reflects the viewed user's entity.
  // The useAuthStore.setState calls below mutate admin's userDetail in-memory only
  // (no API write) to keep map context in sync; they are unrelated to identity rendering.
  const userDetail = useEffectiveUserDetail()
  const hasSortSpaceResultsFeature = hasFeature(userDetail, FeatureFlag.SORT_SPACE_RESULTS)
  const hasHospitalityGroupsFilterFeature = hasFeature(userDetail, FeatureFlag.HOSPITALITY_GROUPS_FILTER)

  const venueGroupsForCity = userDetail?.active_entity?.venue_groups ?? []
  const eventVenueGroup = eventData?.venue_group_id
    ? venueGroupsForCity.find((vg: any) => vg.id === eventData.venue_group_id)
    : undefined
  const city = eventVenueGroup?.name || eventData?.location || ''

  // Get real space count from DB for the matched venue group
  const { count: realSpaceCount } = useSpaces({
    active_venue_group_id: eventVenueGroup?.id ?? 0,
    page: 1,
  })

  const context: EventContext = useMemo(() => ({
    name: eventData?.name || 'Untitled Event',
    guests: eventData?.num_attendees || 50,
    city,
    venueGroupName: userDetail?.current_venue_group?.name,
    count: Math.max(realSpaceCount ?? 0, getCityVenueCount(city)),
    privacy: eventData?.privacy_requirement || 'Private',
    eventType: eventData?.event_type || 'Dinner',
    distance: eventData?.location_max_distance ? `${eventData.location_max_distance} mi` : '0.5 mi',
    cityTier: (getCityTier(city) as 'premium' | 'essential') || 'essential',
  }), [eventData, city, userDetail?.current_venue_group?.name, realSpaceCount])

  // ── Compute event summary for header display ──────────────────────────
  const eventCenter = useMemo(() => {
    if (eventData?.latitude != null && eventData?.longitude != null) {
      return { lat: eventData.latitude, lng: eventData.longitude }
    }
    return undefined
  }, [eventData?.latitude, eventData?.longitude])

  const eventSummary = useMemo(() => {
    if (!eventData) return { dateRange: undefined, attendees: undefined }

    const date = eventData.event_space_list_dates?.[0]
    let dateRange = formatUtcDateLongFromUnknown(date?.start_date, 'en-US')

    if (dateRange && eventData.display_json && typeof eventData.display_json === 'object') {
      const startTime = 'start_time' in eventData.display_json ? String(eventData.display_json.start_time) : undefined
      const endTime = 'end_time' in eventData.display_json ? String(eventData.display_json.end_time) : undefined
      if (startTime || endTime) {
        const timeStr = [startTime, endTime].filter(Boolean).join(' - ')
        dateRange = `${dateRange}  ${timeStr}`
      }
    }

    let attendees: string | undefined = undefined
    if (typeof eventData.num_attendees === 'number' && eventData.num_attendees > 0) {
      const formatMap: Record<string, string> = { seated: 'seated', standing: 'standing', mix: 'mix' }
      const typeLabel = eventData.event_type || (eventData.event_format && formatMap[eventData.event_format] ? formatMap[eventData.event_format] : undefined)
      attendees = typeLabel ? `${eventData.num_attendees} ${typeLabel}` : `${eventData.num_attendees}`
    }

    return { dateRange, attendees }
  }, [eventData])

  // ── Dynamic document title ───────────────────────────────────────────
  useEffect(() => {
    if (!eventData?.name) return
    const parts = [eventData.name]
    if (eventData.num_attendees) parts.push(`${eventData.num_attendees} guests`)
    if (city) parts.push(city)
    document.title = `${parts.join(' - ')} - Revel Street`
  }, [eventData?.name, eventData?.num_attendees, city])

  // ── Sync active venue group from event data on every load ─────────────
  // Scoped to this event view — updates store state in-memory only (no
  // localStorage or API persistence), re-derived fresh from the event on
  // each visit and after any auth-store refresh. The "already matches"
  // guard is the only loop-breaker needed: once correct, deps don't change.
  useEffect(() => {
    // Do not mutate the admin's userDetail while impersonating. The
    // venue-group sync must only apply to the admin's own session.
    if (useAuthStore.getState().viewAsUserDetail !== null) return

    const vgs = userDetail?.active_entity?.venue_groups ?? []
    if (!eventData || vgs.length === 0) return

    const eventVgId = eventData.venue_group_id
    if (eventVgId) {
      if (eventVgId !== userDetail?.active_venue_group) {
        const matchingGroup = vgs.find((vg: any) => vg.id === eventVgId)
        useAuthStore.setState(((s: { userDetail: any }) => ({
          userDetail: s.userDetail
            ? { ...s.userDetail, active_venue_group: eventVgId, ...(matchingGroup ? { current_venue_group: matchingGroup } : {}) }
            : null,
        })) as any)
      }
      return
    }

    const eventLocation = eventData.location
    if (eventLocation) {
      const matchingGroup = vgs.find(
        (vg: any) => vg.name.toLowerCase() === eventLocation.toLowerCase(),
      )
      if (matchingGroup && matchingGroup.id !== userDetail?.active_venue_group) {
        useAuthStore.setState(((s: { userDetail: any }) => ({
          userDetail: s.userDetail
            ? { ...s.userDetail, active_venue_group: matchingGroup.id, current_venue_group: matchingGroup }
            : null,
        })) as any)
      }
    }
  }, [eventData?.venue_group_id, eventData?.location, userDetail?.active_entity?.venue_groups, userDetail?.active_venue_group])

  // ── Derive shortlisted space IDs from event memberships ───────────────
  const [optimisticShortlistedIds, setOptimisticShortlistedIds] = useState<Set<number>>(new Set())
  const shortlistedSpaceIds = useMemo(() => {
    const ids = new Set<number>()
    if (eventData?.space_memberships) {
      for (const m of eventData.space_memberships as any[]) {
        if (m.space_id) ids.add(m.space_id)
      }
    }
    for (const id of optimisticShortlistedIds) ids.add(id)
    return ids
  }, [eventData?.space_memberships, optimisticShortlistedIds])

  // ── State ─────────────────────────────────────────────────────────────
  const showRecsEmptyState = context.cityTier === 'premium'
  const defaultTab: VrmTab = (search.fromCreate && !showRecsEmptyState) ? 'recs' : 'search'
  const activeTab: VrmTab = search.tab ?? defaultTab
  const [editEventOpen, setEditEventOpen] = useState(false)

  const [recsPhase, setRecsPhase] = useState<RecsPhase>(
    showRecsEmptyState ? 'empty' : 'input',
  )
  const [summaryChips, setSummaryChips] = useState<string[]>([])
  const [lastBrief, setLastBrief] = useState<string>('')
  const [lastVenues, setLastVenues] = useState<VenueSuggestion[]>([])
  // Track whether we've already synced the initial phase from persisted data
  const [initialSynced, setInitialSynced] = useState(false)
  // Track which event we last synced for (reset on navigation)
  const lastSyncedEventIdRef = useRef<number | undefined>(undefined)

  // ── Reset recs state when navigating between events ───────────────────
  useEffect(() => {
    if (numericEventId !== lastSyncedEventIdRef.current) {
      lastSyncedEventIdRef.current = numericEventId
      setInitialSynced(false)
      setRecsPhase(showRecsEmptyState ? 'empty' : 'input')
      setSummaryChips([])
      setLastBrief('')
      setLastVenues([])
    }
  }, [numericEventId, showRecsEmptyState])

  // ── Filter out recs whose space is already on the user's shortlist ────
  // Recs added to the shortlist via any path (e.g. from the search tab) should
  // not appear in the Revel Recs tab — they belong in My Shortlist instead.
  const visibleRecsList = useMemo(() => {
    if (!recsList) return recsList
    return recsList.filter((r) => !shortlistedSpaceIds.has(r.space_id))
  }, [recsList, shortlistedSpaceIds])

  // ── Resolve venue data for recs display ───────────────────────────────
  const { data: recsWithSpaces, isLoading: recsSpacesLoading } = useRecsSpaceData(
    recsPhase === 'results' || recsPhase === 'complete' ? visibleRecsList : undefined,
  )

  // ── Sync phase from persisted status on first load ────────────────────
  useEffect(() => {
    if (!recsStatus || initialSynced) return
    setInitialSynced(true)

    if (recsStatus.briefSubmitted) {
      const hasActiveRecs = recsStatus.hasRecs
      const hasReviewedRecs = (recsStatus.shortlistedRecCount ?? 0) > 0 || (recsStatus.downvotedRecCount ?? 0) > 0

      if (hasActiveRecs) {
        // There are still recs to review
        setRecsPhase('results')
      } else if (hasReviewedRecs) {
        // All recs have been reviewed → completion state
        setRecsPhase('complete')
      } else {
        // Brief submitted but no recs delivered yet
        setRecsPhase('in-progress')
      }

      if (recsStatus.tags && recsStatus.tags.length > 0) {
        setSummaryChips(recsStatus.tags)
      }
      if (recsStatus.brief) {
        setLastBrief(recsStatus.brief)
      }
      if (recsStatus.refVenues && recsStatus.refVenues.length > 0) {
        setLastVenues(recsStatus.refVenues.map((v) => ({ name: v.name, city: v.city, type: 'Venue', venueId: v.id })))
      }
    }
    // Otherwise stay on 'input' (the default)
  }, [recsStatus, initialSynced])

  // ── Transition to results when recs become ready ──────────────────────
  useEffect(() => {
    if (
      recsPhase === 'in-progress' &&
      recsList &&
      recsList.length > 0
    ) {
      setRecsPhase('results')
    }
  }, [recsPhase, recsList])

  // ── Correct phase when showRecsEmptyState becomes true after mount ────
  // Guard: once the initial phase sync has run the user is in control of
  // navigation, so we must not override their intent (e.g. clicking
  // "Get My Recs" sets phase to 'input' before a brief is submitted).
  useEffect(() => {
    if (initialSynced) return
    if (showRecsEmptyState && recsPhase === 'input' && !recsStatus?.briefSubmitted) {
      setRecsPhase('empty')
    }
  }, [initialSynced, showRecsEmptyState, recsPhase, recsStatus?.briefSubmitted])

  // ── Badge counts derived from real data ───────────────────────────────
  const recsCount = visibleRecsList?.length ?? 0
  const newRecsCount = useMemo(() => {
    if (!visibleRecsList) return 0
    const cutoff = Date.now() - 72 * 60 * 60 * 1000
    return visibleRecsList.filter((r) => new Date(r.created_at).getTime() > cutoff).length
  }, [visibleRecsList])

  // ── Revel Recs completion state ─────────────────────────────────────
  // Use counts from the status query (which includes shortlisted/downvoted)
  // because `recsList` only contains active recs. Recs filtered out because
  // their space is already on the shortlist (added via the search tab) count
  // toward "shortlisted" so the completion state fires correctly.
  const recsCompletionCounts = useMemo(() => {
    const active = visibleRecsList?.length ?? 0
    const filteredToShortlist = (recsList?.length ?? 0) - active
    const shortlisted = (recsStatus?.shortlistedRecCount ?? 0) + filteredToShortlist
    const downvoted = recsStatus?.downvotedRecCount ?? 0
    return { active, shortlisted, downvoted }
  }, [recsList, visibleRecsList, recsStatus])

  // Transition to complete state when all recs have been reviewed.
  // Guard: only fire when recsList has actually loaded (not undefined),
  // to avoid a race where recsStatus loads first with old shortlisted counts.
  useEffect(() => {
    if (
      recsPhase === 'results' &&
      recsList !== undefined &&
      recsCompletionCounts.active === 0 &&
      (recsCompletionCounts.shortlisted > 0 || recsCompletionCounts.downvoted > 0)
    ) {
      setRecsPhase('complete')
    }
  }, [recsPhase, recsList, recsCompletionCounts])

  // ── Handlers ──────────────────────────────────────────────────────────



  const handleShortlistRec = async (rec: any, spaceData: any) => {
    if (!isRealEvent) return
    const spaceId = spaceData?.spaceId as number | undefined
    if (spaceId) setOptimisticShortlistedIds((prev) => new Set(prev).add(spaceId))
    try {
      // 1) Mark the rec as shortlisted
      await shortlistRecMutation.mutateAsync({ recId: rec.id, eventId: numericEventId! })
      // 2) Add the space to the event shortlist (if we have a valid spaceId)
      if (spaceId) {
        await addSpaceToEventMutation.mutateAsync({
          eventId: numericEventId!,
          spaceId,
        })
      }
      toast.success(
        `Added ${spaceData?.title || 'venue'} to your shortlist`,
        { position: 'bottom-right' },
      )
    } catch (err) {
      // Roll back optimistic update on failure
      if (spaceId) setOptimisticShortlistedIds((prev) => { const next = new Set(prev); next.delete(spaceId); return next })
      console.error('[handleShortlistRec] Error:', err)
      toast.error('Failed to add to shortlist', { position: 'bottom-right' })
    }
  }

  const handleDownvoteRec = async (rec: any, _spaceData: any) => {
    if (!isRealEvent) return
    try {
      await downvoteRecMutation.mutateAsync({ recId: rec.id, eventId: numericEventId! })
      toast.success('Thanks for the feedback - we\'ll refine your recs', { position: 'bottom-right' })
    } catch (err) {
      console.error('[handleDownvoteRec] Error:', err)
      toast.error('Failed to submit feedback', { position: 'bottom-right' })
    }
  }

  const handleSubmitBrief = async (venues: VenueSuggestion[], brief: string, privacy: string) => {
    if (!isRealEvent) return

    setRecsPhase('loading')
    const chips = extractChips(brief, venues, context)
    setSummaryChips(chips)
    setLastBrief(brief)
    setLastVenues(venues)

    // Persist the brief + venue refs + loading animation in parallel
    await Promise.all([
      submitBriefMutation.mutateAsync({
        eventId: numericEventId!,
        brief: {
          revel_recs_brief: brief,
          revel_recs_brief_tags: chips,
          privacy_requirement: privacy || undefined,
        },
      }),
      venues.length > 0
        ? saveVenueRefsMutation.mutateAsync({
          eventId: numericEventId!,
          venues: venues.map((v) => ({
            name: v.name,
            city: v.city,
            venueId: v.venueId,
            googlePlaceId: v.googlePlaceId,
          })),
        })
        : Promise.resolve(),
      // Minimum animation duration so the loading state feels intentional
      new Promise((r) => setTimeout(r, 3200)),
    ])

    setRecsPhase('in-progress')
  }

  const handleShortlistFromSearch = async (spaceId: number, spaceName: string) => {
    if (!isRealEvent) return
    setOptimisticShortlistedIds((prev) => new Set(prev).add(spaceId))
    try {
      await addSpaceToEventMutation.mutateAsync({ eventId: numericEventId!, spaceId })
      toast.success(`Added ${spaceName} to your shortlist`, { position: 'bottom-right' })
    } catch (err) {
      // Roll back optimistic update on failure
      setOptimisticShortlistedIds((prev) => { const next = new Set(prev); next.delete(spaceId); return next })
      console.error('[handleShortlistFromSearch] Error:', err)
      toast.error('Failed to add to shortlist', { position: 'bottom-right' })
    }
  }

  const setActiveTab = (tab: VrmTab) => {
    void navigate({
      search: { ...search, tab },
      replace: true,
    } as any)
  }

  const handleGoToSearch = () => setActiveTab('search')

  const handleTabClick = (tab: VrmTab) => {
    setActiveTab(tab)
    // Scroll content panels to top to avoid landing at the bottom
    window.scrollTo(0, 0)
    // Also scroll any overflow-y-auto containers
    document.querySelectorAll('[class*="overflow-y-auto"]').forEach((el) => {
      el.scrollTop = 0
    })
  }

  // ── Listings store hydration & URL sync (when search tab is shown) ──
  const hydrate = useListingsStore((s) => s.hydrate)
  const resetFilters = useListingsStore((s) => s.resetFilters)
  const setAutoFilterBanner = useListingsStore((s) => s.setAutoFilterBanner)
  const hydratedRef = useRef(false)
  const prevEventSlugRef = useRef(eventSlug)
  const prevEventLocationRef = useRef<string | null>(null)
  const spaceBeingAddedToEvent = useListingsStore(selectSpaceBeingAddedToEvent)
  const setSpaceBeingAddedToEvent = useListingsStore((s) => s.setSpaceBeingAddedToEvent)

  // On fresh mount of events.$eventSlug, clear any listings store state left
  // over from a different route (e.g. /collections/<city>). Run synchronously
  // via useState initializer so the clear happens BEFORE the first render
  // commits — otherwise the map would mount with stale pins from the previous
  // route, latch its fitBounds onto them, and the subsequent re-fit would be
  // suppressed by the hasFittedRef latch (especially while the search tab is
  // initially hidden).
  useState(() => {
    useListingsStore.getState().hydrate(null, 1, undefined)
    return null
  })

  // Read activeVenueGroupId from the store. Used to gate ListingsMap so it
  // only mounts once the store's city context matches this event — preventing
  // a brief render with the previous route's pins.
  const activeVenueGroupIdInStore = useListingsStore(selectActiveVenueGroupId)
  const isStoreReadyForThisEvent =
    activeVenueGroupIdInStore !== undefined &&
    activeVenueGroupIdInStore === eventData?.venue_group_id

  // Reset filters & hydration when switching between events.
  //
  // Two important things happen here that the location-change effect below
  // alone does NOT handle:
  //   1. We call hydrate(null, 1, undefined) — this clears activeVenueGroupId
  //      so the gated typesense query stops running for the previous city
  //      until the NEW city's id is known. Otherwise pageItems from the
  //      previous city would briefly stay visible.
  //   2. We drop URL filter params (location/radius/etc) so they don't poison
  //      the next hydrate. Without this, Chi's lat/lng/radius URL params would
  //      apply to a NY query and return 0 results.
  useEffect(() => {
    if (eventSlug !== prevEventSlugRef.current) {
      prevEventSlugRef.current = eventSlug
      hydratedRef.current = false
      resetFilters()
      // Clear activeVenueGroupId + cached pins so the listings query gates off
      // until the NEW event's venue_group_id arrives.
      hydrate(null, 1, undefined)
      // Drop URL filter params — keep only the tab. Prevents previous-city
      // filters (lat/lng/radius/neighborhood) from poisoning the new query.
      void navigate({ search: search.tab ? { tab: search.tab } : {}, replace: true } as any)
      prevEventLocationRef.current = null
    }
  }, [eventSlug, resetFilters, hydrate, navigate, search.tab])

  // Re-hydrate when the event's location changes in-place (e.g. the user
  // edited the city via the Edit Event dialog). Without this, stale
  // lat/lng/radius/neighborhood filters from the old city persist and the
  // map stays centered on it.
  useEffect(() => {
    if (!eventData) return
    const key = `${eventData.venue_group_id ?? ''}|${eventData.latitude ?? ''}|${eventData.longitude ?? ''}`
    if (prevEventLocationRef.current === null) {
      prevEventLocationRef.current = key
      return
    }
    if (prevEventLocationRef.current === key) return
    prevEventLocationRef.current = key
    hydratedRef.current = false
    resetFilters()
    // Drop URL filter params so the re-hydration doesn't pick up old coords
    void navigate({ search: search.tab ? { tab: search.tab } : {}, replace: true } as any)
  }, [eventData?.venue_group_id, eventData?.latitude, eventData?.longitude, resetFilters, navigate, search.tab])

  // Hydrate: prefer URL filter params (shareable link), else derive from event
  // Only pre-load filters from event data when arriving from the homepage
  // event creation flow (fromCreate=true). All other navigations start blank.
  useEffect(() => {
    if (activeTab === 'search' && !hydratedRef.current && !eventLoading) {
      // Extract only listings-filter keys (excludes recs-specific params like name, guests, city)
      const filterParams = validateSearchParams(search as Record<string, unknown>)
      const urlFilters = urlParamsToFilters(filterParams)

      let filters: FiltersState | null
      if (urlFilters) {
        // URL already has explicit filter params (e.g. shareable link) — use them
        filters = urlFilters
      } else if (search.fromCreate) {
        // User just created an event from the homepage — pre-load filters from event data
        filters = buildFiltersFromEvent(eventData, eventData?.venue_group_id ?? undefined)
      } else {
        // All other navigations — blank filters, map centers via eventCenter prop
        filters = null
      }

      hydrate(filters, filterParams.page || 1, eventData?.venue_group_id ?? undefined, filterParams.sortBy)
      hydratedRef.current = true

      // Show auto-filter banner when arriving from homepage event creation
      if (search.fromCreate && filters) {
        const filterCount = countAppliedFilters(filters, { includeHospitalityGroupsFilter: hasHospitalityGroupsFilterFeature })
        if (filterCount > 0) {
          setAutoFilterBanner({ count: filterCount, eventName: context.name })
        }
      }
    }
  }, [activeTab, hydrate, setAutoFilterBanner, eventLoading, eventData, context, search, hasHospitalityGroupsFilterFeature])

  // Sync filter changes back to URL (only when search tab is active)
  const filters = useListingsStore(selectFilters)
  const currentPage = useListingsStore(selectCurrentPage)
  const sortBy = useListingsStore(selectSortBy)
  const lastAppliedAt = useListingsStore((s) => s.lastAppliedAt)
  const navigateRef = useRef(navigate)
  useEffect(() => { navigateRef.current = navigate }, [navigate])
  const searchRef = useRef(search)
  useEffect(() => { searchRef.current = search }, [search])

  useEffect(() => {
    if (!hydratedRef.current || activeTab !== 'search' || !lastAppliedAt) return
    const filterParams = filtersToUrlParams(filters, { includeHospitalityGroupsFilter: hasHospitalityGroupsFilterFeature })
    navigateRef.current({
      search: {
        ...searchRef.current,
        ...filterParams,
        page: currentPage > 1 ? currentPage : undefined,
        sortBy: hasSortSpaceResultsFeature ? sortByToUrlParam(sortBy) : undefined,
        tab: 'search' as const,
      },
      replace: true,
    } as any)
  }, [filters, currentPage, sortBy, lastAppliedAt, activeTab, hasSortSpaceResultsFeature, hasHospitalityGroupsFilterFeature])

  // ── Transition screen ─────────────────────────────────────────────────



  // ══════════════════════════════════════════════════════════════════════════
  // RENDER
  // ══════════════════════════════════════════════════════════════════════════

  if (
    eventAccessQuery.data?.access === 'forbidden' ||
    eventAccessQuery.data?.access === 'not_found'
  ) {
    return <NotFound />
  }

  if (eventAccessQuery.isLoading && !eventData) {
    return (
      <div className="flex h-full min-h-0 flex-1 items-center justify-center bg-[#FEFBF6]">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col overflow-hidden bg-[#FEFBF6]">
      {/* ── Event Sub-Header ─────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e4e1e9] bg-[#fdf6ef] px-6 py-3">
        <div className="flex items-center gap-6">
          <span className="font-serif text-[22px] font-medium text-[#322f36] leading-tight">
            {eventLoading ? (
              <span className="inline-block h-7 w-48 animate-pulse rounded bg-gray-100" />
            ) : (
              context.name
            )}
          </span>

          {eventData && (
            <div className="flex items-center gap-5">
              {eventData.location && (
                <div className="flex items-center gap-1.5 text-[13px] text-[#322f36]">
                  <MapPin className="h-4 w-4 text-[#95919a] flex-shrink-0" />
                  {shortenLocation(eventData.location)}
                </div>
              )}
              {eventSummary.dateRange && (
                <div className="flex items-center gap-1.5 text-[13px] text-[#322f36]">
                  <CalendarDays className="h-4 w-4 text-[#95919a] flex-shrink-0" />
                  {eventSummary.dateRange}
                </div>
              )}
              {eventSummary.attendees && (
                <div className="flex items-center gap-1.5 text-[13px] text-[#322f36]">
                  <Users className="h-4 w-4 text-[#95919a] flex-shrink-0" />
                  {eventSummary.attendees}
                </div>
              )}
            </div>
          )}
        </div>

        {isRealEvent && eventData && (
          <>
            <Button variant="revelOutline" size="revel28" onClick={() => setEditEventOpen(true)}>
              <Pencil className="h-3.5 w-3.5 mr-1.5" />
              Modify event
            </Button>
            <EditEventDialog open={editEventOpen} onOpenChange={setEditEventOpen} event={eventData || null} />
          </>
        )}
      </div>

      {/* ── Tabs ─────────────────────────────────────────────────────────── */}
      <div className="flex flex-shrink-0 items-center gap-1 border-b border-[#e4e1e9] bg-[#FEFBF6] px-4">
        {/* Search tab */}
        <button
          onClick={() => handleTabClick('search')}
          className={`cursor-pointer border-b-2 bg-transparent px-2 py-3 font-inherit text-sm transition-all duration-150 ${activeTab === 'search'
            ? 'border-[#322f36] font-semibold text-[#322f36]'
            : 'border-transparent font-normal text-[#95919a] hover:text-[#6a6262]'
            }`}
        >
          Search venue spaces
        </button>

        {/* Revel Recs tab with count badge */}
        <button
          onClick={() => handleTabClick('recs')}
          className={`cursor-pointer border-b-2 bg-transparent px-2 py-3 font-inherit text-sm transition-all duration-150 flex items-center gap-1.5 ${activeTab === 'recs'
            ? 'border-[#322f36] font-semibold text-[#322f36]'
            : 'border-transparent font-normal text-[#95919a] hover:text-[#6a6262]'
            }`}
        >
          Revel Recs
          {recsCount > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e8e5ec] px-1.5 text-[11px] font-medium text-[#6a6262]">
              {recsCount}
            </span>
          )}
        </button>

        {/* My Shortlist tab */}
        <button
          onClick={() => handleTabClick('shortlist')}
          className={`cursor-pointer border-b-2 bg-transparent px-2 py-3 font-inherit text-sm transition-all duration-150 ml-2 flex items-center gap-1.5 ${activeTab === 'shortlist'
            ? 'border-[#322f36] font-semibold text-[#322f36]'
            : 'border-transparent font-normal text-[#95919a] hover:text-[#6a6262]'
            }`}
        >
          My shortlist
          {shortlistedSpaceIds.size > 0 && (
            <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#e8e5ec] px-1.5 text-[11px] font-medium text-[#6a6262]">
              {shortlistedSpaceIds.size}
            </span>
          )}
        </button>
      </div>

      {/* ── Tab Content ──────────────────────────────────────────────────── */}
      <TabPanel active={activeTab === 'search'} keepMounted={KEEP_TABS_MOUNTED} className="flex flex-1 min-h-0 bg-[#fefbf6]">
        <ListingsFiltersPanel />
        <ListingsResultsPanel onShortlist={isRealEvent ? handleShortlistFromSearch : undefined} shortlistedSpaceIds={shortlistedSpaceIds} />
        <div className="h-full w-full overflow-hidden">
          {!eventLoading && isStoreReadyForThisEvent && (
            <ListingsMap
              debounceMs={500}
              active={activeTab === 'search'}
              onShortlist={isRealEvent ? handleShortlistFromSearch : undefined}
              shortlistedSpaceIds={shortlistedSpaceIds}
              eventCenter={eventCenter}
            />
          )}
        </div>
      </TabPanel>

      <TabPanel active={activeTab === 'recs'} keepMounted={KEEP_TABS_MOUNTED} className="flex flex-1 min-h-0">
        <div className="flex flex-1 min-h-0">
          {/* Left: Recs content */}
          <div className="flex-1 min-w-0 overflow-y-auto border-r border-[#e4e1e9] max-w-[800px]">
            <div className="px-7 pb-20 pt-7">
              {/* Show spinner while event or recsStatus is still loading */}
              {eventLoading || (!initialSynced && isRealEvent) ? (
                <div className="flex items-center justify-center py-20">
                  <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-200 border-t-violet-600" />
                </div>
              ) : (
                <>
                  {recsPhase === 'empty' && (
                    <RecsPremiumEmptyState onGetStarted={() => setRecsPhase('input')} />
                  )}

                  {recsPhase === 'loading' && <RecsLoadingState />}

                  {recsPhase === 'in-progress' && (
                    <RecsSummaryState
                      chips={summaryChips}
                      context={context}
                      onBrowse={handleGoToSearch}
                    />
                  )}

                  {recsPhase === 'results' && (
                    <RecsResultsState
                      recsWithSpaces={recsWithSpaces}
                      context={context}
                      onBrowse={handleGoToSearch}
                      onShortlist={handleShortlistRec}
                      onDownvote={handleDownvoteRec}
                      isLoading={recsSpacesLoading}
                      shortlistedSpaceIds={shortlistedSpaceIds}
                    />
                  )}

                  {recsPhase === 'complete' && (
                    <RecsCompletionState
                      shortlistedCount={recsCompletionCounts.shortlisted}
                      downvotedCount={recsCompletionCounts.downvoted}
                      onViewShortlist={() => setActiveTab('shortlist')}
                    />
                  )}
                  {recsPhase === 'input' && (
                    <RecsInputForm
                      context={context}
                      onSubmit={handleSubmitBrief}
                      onBrowseClick={handleGoToSearch}
                      initialBrief={lastBrief}
                      initialVenues={lastVenues}
                      isEditing={!!lastBrief}
                      onCancel={
                        lastBrief
                          ? () => {
                            const hasAnyRecs = recsCompletionCounts.active > 0 || recsCompletionCounts.shortlisted > 0 || recsCompletionCounts.downvoted > 0
                            if (hasAnyRecs) {
                              const allReviewed = recsCompletionCounts.active === 0 && (recsCompletionCounts.shortlisted > 0 || recsCompletionCounts.downvoted > 0)
                              setRecsPhase(allReviewed ? 'complete' : 'results')
                            } else {
                              setRecsPhase('in-progress')
                            }
                          }
                          : showRecsEmptyState
                            ? () => setRecsPhase('empty')
                            : undefined
                      }
                    />
                  )}
                </>
              )}
            </div>
          </div>
          {/* Right: Map */}
          <div className="flex flex-1 h-full overflow-hidden">
            {!eventLoading && context.city && <RecsMap key={context.city} city={context.city} recs={recsWithSpaces} active={activeTab === 'recs'} />}
          </div>
        </div>
      </TabPanel>

      <TabPanel active={activeTab === 'shortlist' && isRealEvent} keepMounted={KEEP_TABS_MOUNTED} className="flex-1 min-h-0 overflow-hidden">
        {isRealEvent && (
          <EventViewContent eventSlug={eventSlug} embedded onNavigateToSearch={handleGoToSearch} />
        )}
      </TabPanel>

      {/* ── Add-to-event dialog (for search tab) ───────────────────────── */}
      <AddEventToSpaceDialog
        startStep="add-to-event"
        open={!!spaceBeingAddedToEvent}
        onOpenChange={() => setSpaceBeingAddedToEvent(null)}
        spaceId={spaceBeingAddedToEvent?.spaceId as number}
        onCreateEvent={({ eventName }) => {
          if (!spaceBeingAddedToEvent?.spaceName) return
          toast.success(
            `Successfully added ${spaceBeingAddedToEvent.spaceName} to ${eventName}`,
            { richColors: true, position: 'bottom-right' },
          )
        }}
        onSelectEvent={() => { }}
        shouldAddSpaceOnCreate={true}
      />
    </div>
  )
}
